import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ordersAPI } from '../services/api';

const OrderSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await ordersAPI.getById(id);
        setOrder(res.data);
      } catch (e) {
        setError('Could not load order');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const share = async () => {
    if (!order) return;
    const url = `${window.location.origin}/share/orders/${order.id}`;
    const title = `Order ID: ${order.id} placed successfully`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Tracking link copied to clipboard');
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{error || 'Order not found'}</h1>
          <button onClick={() => navigate('/dashboard')} className="text-blue-600 hover:text-blue-800">Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🎉 Order placed successfully!</h1>
            <p className="text-gray-600 mt-1">Order ID: #{order.id}</p>
          </div>
          <button onClick={share} className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full">Share tracking</button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="bg-green-50 border border-green-200 rounded p-4">
            <p className="text-sm text-green-700">Status</p>
            <p className="font-semibold text-green-800 mt-1 capitalize">{order.status}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <p className="text-sm text-blue-700 font-medium mb-2">Order Summary</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-blue-600 text-xs">
                <span>Subtotal:</span>
                <span>₹{order.items_total || (parseFloat(order.total_amount || 0) - (order.delivery_charge || 50)).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-blue-600 text-xs">
                <span>Delivery:</span>
                <span>+ ₹{order.delivery_charge || 50}</span>
              </div>
              <div className="flex justify-between font-bold text-blue-800 border-t border-blue-200 pt-1 mt-1">
                <span>Total:</span>
                <span>{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</span>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded p-4">
            <p className="text-sm text-purple-700">Items</p>
            <p className="font-semibold text-purple-800 mt-1">{order.order_items.length}</p>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Items</h2>
          <div className="space-y-3">
            {order.order_items.map(item => (
              <div key={item.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded">
                <img src={item.product.image_url} alt={item.product.name} className="w-12 h-12 rounded object-cover" onError={(e) => { e.target.src = 'https://via.placeholder.com/64'; }} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{item.product.name}</p>
                  <p className="text-sm text-gray-600">Qty {item.quantity} • {item.product.formatted_price}</p>
                </div>
                <div className="text-sm">{item.formatted_total_price}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/orders/${order.id}`} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full">View in Orders</Link>
          <Link to={`/share/orders/${order.id}`} className="px-4 py-2 bg-gray-800 hover:bg-black text-white rounded-full">Public tracking link</Link>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full">Continue Shopping</button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;


